import {
  changelogRouteConfig,
  clientsRouteConfig,
  insightsRouteConfig,
  launchesRouteConfig,
  proposalChangesRouteConfig,
  proposalEditorRouteConfig,
  recentChecksRouteConfig,
} from 'src/app/graph/routes';
import { absoluteURL } from 'src/lib/absoluteURL';
import { locationToPath } from 'src/lib/routeConfig/locationToPath';

import { GraphRef, graphRefToString } from '../../hooks/useGraphRef';
import { useInternalGraphLinking } from '../../hooks/useInternalGraphLinking';

type shortcodesConfigInput = {
  graphTitle?: string;
  graphRef: GraphRef;
  variantUrl?: string | undefined | null;
  graphVisibilityType: 'public' | 'graph';
  internalGraphLinking: ReturnType<typeof useInternalGraphLinking>;
};

export const shortcodesConfig: Array<{
  id: string;
  name: string;
  value: (input: shortcodesConfigInput) => string;
  displayContext: 'proposals' | 'not-proposals' | 'all';
}> = [
  {
    id: 'graph.name',
    name: 'graph name',
    value: ({ graphTitle }) => graphTitle ?? 'No graph name set',
    displayContext: 'all',
  },
  {
    id: 'graph.ref',
    name: 'graph ref',
    value: ({ graphRef }) => graphRefToString(graphRef),
    displayContext: 'all',
  },
  {
    id: 'graph.url.endpoint',
    name: 'endpoint',
    value: ({ variantUrl }) => variantUrl ?? 'No endpoint set',
    displayContext: 'not-proposals',
  },
  {
    id: 'graph.url.changelog',
    name: 'changelog',
    value: ({ graphRef, graphVisibilityType }) =>
      absoluteURL(
        changelogRouteConfig.path({ ...graphRef, graphVisibilityType }),
      ),
    displayContext: 'not-proposals',
  },
  {
    id: 'graph.url.changes',
    name: 'changes',
    value: ({ graphRef, graphVisibilityType }) =>
      absoluteURL(
        proposalChangesRouteConfig.path({ ...graphRef, graphVisibilityType }),
      ),
    displayContext: 'proposals',
  },
  {
    id: 'graph.url.editor',
    name: 'editor',
    value: ({ graphRef, graphVisibilityType }) =>
      absoluteURL(
        proposalEditorRouteConfig.path({ ...graphRef, graphVisibilityType }),
      ),
    displayContext: 'proposals',
  },
  {
    id: 'graph.url.reference',
    name: 'schema reference',
    value: ({ internalGraphLinking: { locationToReference } }) =>
      absoluteURL(locationToPath(locationToReference({}))),
    displayContext: 'all',
  },
  {
    id: 'graph.url.sdl',
    name: 'SDL',
    value: ({ internalGraphLinking: { locationToSDL } }) =>
      absoluteURL(locationToPath(locationToSDL({}))),
    displayContext: 'not-proposals',
  },
  {
    id: 'graph.url.explorer',
    name: 'explorer',
    value: ({ internalGraphLinking: { locationToExplorer } }) =>
      absoluteURL(locationToPath(locationToExplorer({}))),
    displayContext: 'not-proposals',
  },
  {
    id: 'graph.url.clients',
    name: 'clients',
    value: ({ graphRef, graphVisibilityType }) =>
      graphRef &&
      absoluteURL(
        clientsRouteConfig.path({ ...graphRef, graphVisibilityType }),
      ),
    displayContext: 'not-proposals',
  },
  {
    id: 'graph.url.operations',
    name: 'operations',
    value: ({ graphRef, graphVisibilityType }) =>
      graphRef &&
      absoluteURL(
        insightsRouteConfig.path({ ...graphRef, graphVisibilityType }),
      ),
    displayContext: 'not-proposals',
  },
  {
    id: 'graph.url.errors',
    name: 'errors',
    value: ({ graphRef, graphVisibilityType }) =>
      graphRef &&
      absoluteURL(
        insightsRouteConfig.path({
          ...graphRef,
          graphVisibilityType,
          tab: 'errors',
        }),
      ),
    displayContext: 'not-proposals',
  },
  {
    id: 'graph.url.checks',
    name: 'checks',
    value: ({ graphRef, graphVisibilityType }) =>
      graphRef &&
      absoluteURL(
        recentChecksRouteConfig.path({
          ...graphRef,
          graphVisibilityType,
          view: 'variant',
        }),
      ),
    displayContext: 'not-proposals',
  },
  {
    id: 'graph.url.launches',
    name: 'launches',
    value: ({ graphRef, graphVisibilityType }) =>
      graphRef &&
      absoluteURL(
        launchesRouteConfig.path({ ...graphRef, graphVisibilityType }),
      ),
    displayContext: 'not-proposals',
  },
];

export const operationSpecificShortcodeText = (id: string) => `operation.${id}`;
export const collectionSpecificShortcodeText = (id: string) =>
  `collection.${id}`;
